import configs from '../configs.js';
import { getRgbByPoint, getCxCyByRgbs } from '@/axios/ITianmu.js';
import { transRgbToLight } from '@/components/DesignCanvas/utils/common';
import { SAVE_TYPE, SHAPE_SUB_TYPE } from '../../../shapes/enums.js';

class Power {
  constructor(props) {
    this.id = props.power_id;
    this.dir = props.access_hole;
    this.energy = 300;
    this.toControllers = (props.to_controllers || []).map(item => ({
      id: item.phy_id,
      toChannel: item.to_channel
    }));

    this.toPower = props.to_power?.negative_to;
  }
}

class Controller {
  constructor(props, isMain) {
    this.isMain = isMain;
    this.id = props.phy_id;
    this.dir = props.access_hole;
    this.channelCount = props.channels;
    this.totalChannelCount = props.total_channels || 0;
    this.classSku = props.class_sku;
    this.deviceName = props.name;
    this.isDanlu = this.channelCount === 1;

    this.totalPoint = 0;
  
    this.powers = [];

    const { pixel_map_physical, pixel_map, usePhysicalPixelMap } = props;
    const usePhysicalMap = !!(
      usePhysicalPixelMap &&
      Array.isArray(pixel_map_physical) &&
      pixel_map_physical.length > 0
    );
    const pixelMap = usePhysicalMap ? pixel_map_physical : pixel_map;
    this.usePhysicalPixelMap = usePhysicalMap;
    if (pixelMap) {      
      this.channels = (pixelMap || []).map(channel => {
        this.totalPoint += channel.length;
        return {
          data: channel,
          pointCount: channel.length
        }
      });
    };

    if (props.slaves) {
      this.slaves = props.slaves.map(item => {
        return new Controller({
          ...item,
          name: props.name,
          class_sku: props.class_sku,
          usePhysicalPixelMap, // 与主控同一意图；子控仍会校验自身是否带有 pixel_map_physical
        }, false)
      })

      const slavesPoint = this.slaves.reduce((pre, next) => { 
        return pre + next.totalPoint;
      }, 0);
      
      this.totalPoint += slavesPoint;
    }
  }
}

class TianmuProxy {
  constructor(props) {
    this.handler(props)
  }

  hasTopController(controllers = []) {
    return controllers.some((controller) => {
      if (controller?.access_hole === 'up') {
        return true;
      }
      return this.hasTopController(controller?.slaves || []);
    });
  }

  handler(data) {
    this.shapeType = data?.shapeType;
    this.plan = data?.plan;
    this.matrixs = this.plan?.split_info?.matrixs || [];
    this.onlyMain = data.onlyMain;
    // 点位预览等工作台场景只需矩阵/尺寸，跳过控制器与电源解析，避免大面积 pixel_map 占满内存
    this.liteMode = !!data.liteMode;
    // 默认 false（pixel_map）；显式 true 且为 circle_bar（或兼容旧数据）时才用 pixel_map_physical
    this.usePhysicalPixelMap = this.resolveUsePhysicalPixelMap(data);
    this.isCustom = data?.plan_project_info?.userCustom === 1;
    this.isBiaopin = data?.shape === SAVE_TYPE.TEMPLATE;
    this.panelInfo = data?.controller_diagram?.panel_info;

    this.setAccessHole(data);
    this.setBlocks(data);
    if (this.liteMode) {
      this.controllers = [];
      this.powers = [];
    } else {
      this.setControllers(data);
      this.setPower(data);
    }
    this.setCanvasSize(data);
  }

  /**
   * 是否使用 pixel_map_physical
   * - 默认 false：走 pixel_map
   * - 调用方显式 usePhysicalPixelMap: true，且 shape 为 circle_bar → 物理坐标
   * - 兼容旧方案无 shape：有 physical_points + radius 时视为圆形线模组
   */
  resolveUsePhysicalPixelMap(data = {}) {
    if (data.usePhysicalPixelMap !== true) return false;

    const shape = data.plan?.shape || data.shape || data.shapeSubType;
    if (shape === SHAPE_SUB_TYPE.CIRCLE_BAR) return true;

    const physicalPoints = data.physical_points || data.controller_diagram?.physical_points || [];
    return physicalPoints.length > 0 && data.plan?.radius > 0;
  }

  setAccessHole(data) {
    const accessHole = new Set();

    (data.plan?.split_info?.matrixs || [])
      .forEach(item => {
        (item.access_hole || '').split('_').forEach(dir => accessHole.add(dir))
      });

    this.accessHole = [...accessHole];

    this.accessHolePaddingTop = 0.2;
    this.accessHolePaddingLeft = 0.2;

    if (this.accessHole.includes('left')) {
      this.accessHolePaddingLeft = 1;
    }

    if (this.accessHole.includes('up')) {
      this.accessHolePaddingTop = 1;
    }

    // onlyMain 场景下，关联标签会跟随 TOP 控制器向上偏移；
    // 即使 split_info.access_hole 未包含 up，也需要给顶部预留空间避免裁剪。
    if (this.onlyMain && this.hasTopController(data.controller_diagram?.masters || [])) {
      this.accessHolePaddingTop = 0.4;
    }

    if (this.shapeType === 'circle') {
      this.accessHolePaddingLeft = 0.8;
    }

    const minLeftRatio = configs.getMinAnnotationPaddingLeftRatio();
    this.accessHolePaddingLeft = Math.max(this.accessHolePaddingLeft, minLeftRatio);
  }

  setBlocks(data) {
    this.blocks = data.controller_diagram?.matrix_values || [];
    this.frameRows = data.controller_diagram?.rows || 0;
    this.frameCols = data.controller_diagram?.cols || 0;
    this.physicalPoints = data.physical_points || data.controller_diagram?.physical_points || [];
  }

  setCanvasSize() {

      const col = (this.blocks && this.blocks[0]) ? this.blocks[0].length : 0;
      const row = this.blocks.length;

      let widthCount = 0;
      let heightCount = 0;
      let onlyMainWidth = 130;
      let onlyMainHeight = 80;

      if (this.accessHole.includes('left')) {
        widthCount += 1;
        onlyMainWidth = 250;
      }

      if (this.accessHole.includes('right')) {
        widthCount += 1;
        onlyMainWidth = 250;
      }

      if (this.accessHole.includes('down')) {
        heightCount += 1;
        onlyMainHeight = 250;
      }

      if (this.accessHole.includes('up')) {
        heightCount += 1;
        onlyMainHeight = 250;
      }

      if (this.shapeType === 'circle') {
        widthCount += 1;
      }

      this.canvasSize = {
        width: configs.containerPadding * this.accessHolePaddingLeft * widthCount + 200 + (configs.block.width + configs.block.marginX) * col,
        height: configs.containerPadding * heightCount + 150 + (configs.block.width + configs.block.marginY) * row
      }

      if (this.onlyMain) {
        this.canvasSize = {
          width: onlyMainWidth + (configs.block.width + configs.block.marginX) * col,
          height: onlyMainHeight + (configs.block.width + configs.block.marginY) * row
        }
      }

      this.canvasSizeNoPadding = {
        width: (configs.block.width + configs.block.marginX) * col,
        height: (configs.block.width + configs.block.marginY) * row
      }
  }

  setControllers(data) {
    const controllers = data.controller_diagram?.masters;
    const controllersRes = [];
    // 入口已按 plan.shape / 覆盖开关算好；此处只往下传，由 Controller 校验自身 physical map
    const usePhysicalPixelMap = !!this.usePhysicalPixelMap;

    controllers?.forEach(controller => {
      controllersRes.push(new Controller({
        ...controller,
        usePhysicalPixelMap,
      }, true));
    });
    // 以控制器是否真正吃到 physical map 为准回写（避免开关 true 但 map 缺失）
    this.usePhysicalPixelMap = controllersRes.some(c => c.usePhysicalPixelMap) ||
      (controllersRes[0]?.slaves || []).some(s => s.usePhysicalPixelMap);

    if (controllersRes[0]) {
      this.channelCount = controllersRes[0].channelCount;
    }
    
    this.controllers = controllersRes;
  }

  setPower(data) {
    const powers = data.power_diagram?.power_connections || [];
    const powersRes = [];

    powers.forEach(item => {
      const power = new Power(item);

      power.toControllers.forEach(data => {
        const controller = this.findControllerById(data.id);

        if (controller) {
          controller.powers.push(power);
        }
      })

      powersRes.push(power);
    });

    this.powers = powersRes;
  }

  findControllerById(id, data = this.controllers) {
    for (const item of data) {
      if (item.id === id) {
        return item;
      }
      
      if (item.slaves && Array.isArray(item.slaves)) {
        const foundInList = this.findControllerById(id, item.slaves);
      
        if (foundInList) {
          return foundInList;
        }
      }
    }
    
    return null;
  }

  findPowerById(id) {
    return this.powers.find(item => item.id === id);
  }

  async getRgbByPoint({ url, blocks = this.blocks }) {
    try {
      const res = await getRgbByPoint({
        matrix_values: blocks,
        pic_url: url
      });

      return res.Result?.rgb_matrix || [];
    } catch (e) {
      console.log(e);
    }
  }

  async getCxCyByRgb(rgbs) {
    try {
      const res = await getCxCyByRgbs({
        matrix_values_rgb: rgbs,
      });

      return res.Result?.matrix_values_cxcy || [];
    } catch (e) {
      console.log(e);
    }
  }

  getLightCxCy(cxcyList, rgbList) {
    const value = (cxcyList || []).map((item, i) => {
      const rgbData = rgbList[i] || [255,255,255];
      const lightValue = ('0' + Number(transRgbToLight({ r: rgbData[0], g: rgbData[1], b: rgbData[2] }, 100)).toString(16)).slice(-2);

      return lightValue + item;
    }).join('');

    return value;
  }

  getChannelsRgbs(blocksRgb) {
    const channelsRgbsList = [];

    console.log('@@@@@@-----this.controllers', this.controllers);
    let channelIndex = 0;
    this.controllers.forEach((controller, index) => {
      controller.channels.forEach((channel) => {
        const data = channel.data;

        channelsRgbsList[channelIndex] = [];

        data.forEach(point => {
          channelsRgbsList[channelIndex].push(blocksRgb[point[1]][point[0]]);
        })

        channelIndex++
      })

      controller.slaves.forEach(controller => {
        controller.channels.forEach(channel => {

          const data = channel.data;

          channelsRgbsList[channelIndex] = [];
          
          data.forEach(point => {
            channelsRgbsList[channelIndex].push(blocksRgb[point[1]][point[0]]);
          });

          channelIndex++
        });
      })
    })
    
    return channelsRgbsList;
  }

  async getSceneData({ deviceList, url, isNew, lightValue }) {
    const newCheckDeviceList = [];
    
    const blocksRgb = await this.getRgbByPoint({ url });

    const channelsRgbs = this.getChannelsRgbs(blocksRgb);
    
    const allCxCy = await this.getCxCyByRgb(channelsRgbs);

    let channelIndex = 0;


    this.controllers.forEach((controller, index) => {
      const device = deviceList[index] || {};

      const deviceInfo = {
        AreaName: device.AreaName,
        ChannelId: device.ChildId,
        DeviceName: device.DeviceName,
        VDeviceId: device.VDeviceId,
        Mac: device.Mac,
        ActionList: [] // item.ActionList.concat([ newActionList ])
      }

      controller.channels.forEach((channel) => {
        const Values = [{
          KeyId: 0, // Channel
          Value: channelIndex,
        }, {
          KeyId: 1, // Mode
          Value: 2,
        }, {
          KeyId: 2, // StartAddr
          Value: 0,
        }, {
          KeyId: 3, // TotalNum
          Value: channel.pointCount
        }, {
          KeyId: 4,
          Value: this.getLightCxCy(allCxCy[channelIndex], channelsRgbs[channelIndex])
        }]

        if (isNew) {
          Values.push({
            KeyId: 5,
            Value: lightValue
          })
        }

        deviceInfo.ActionList.push({
          MsgType:  "0x13010000",
          Values: Values
        });
        
        channelIndex++
      });

      controller.slaves.forEach(controller => {
        controller.channels.forEach(channel => {
          const Values = [{
            KeyId: 0, // Channel
            Value: channelIndex,
          }, {
            KeyId: 1, // Mode
            Value: 2,
          }, {
            KeyId: 2, // StartAddr
            Value: 0,
          }, {
            KeyId: 3, // TotalNum
            Value: channel.pointCount
          }, {
            KeyId: 4,
            Value: this.getLightCxCy(allCxCy[channelIndex], channelsRgbs[channelIndex])
          }]

          if (isNew) {
            Values.push({
              KeyId: 5,
              Value: lightValue
            })
          }

          deviceInfo.ActionList.push({
            MsgType:  "0x13010000",
            Values
          })

          channelIndex++
        })
      });

      newCheckDeviceList.push(deviceInfo);
    })
    
    return newCheckDeviceList;
  }

  async getVideoScenceData({ deviceList, url, lightValue}) {
    const newCheckDeviceList = [];
    // const blocksRgb = await this.getRgbByPoint({ url });

    // const channelsRgbs = this.getChannelsRgbs(blocksRgb);
    
    // const allCxCy = await this.getCxCyByRgb(channelsRgbs);

    let channelIndex = 0;

    this.controllers.forEach((controller, index) => {
      const device = deviceList[index] || {};

      const deviceInfo = {
        AreaName: deviceList.AreaName,
        ChannelId: deviceList.ChildId,
        DeviceName: deviceList.DeviceName,
        VDeviceId: deviceList.VDeviceId,
        Mac: deviceList.Mac,
        ActionList: [] // item.ActionList.concat([ newActionList ])
      }

       deviceInfo.ActionList.push({
          MsgType:  "0x131D0000",
          // Reserve: "0x00000000",
           Values: [{
            KeyId: 0, // Channel
            Value: 0,
          }, {
            KeyId: 1, // Channel
            Value: 0,
          }, {
            KeyId: 2, // Object_Type
            Value: 4294967295,
          }, {
            KeyId: 3, // light
            Value: lightValue || 255,
          }, {
            KeyId: 4, // CheckSum
            Value: 4294967295
          }, {
            KeyId: 5,//FileUrl
            Value: url
          }]
        })

      newCheckDeviceList.push(deviceInfo);
    })
    
    return newCheckDeviceList;
  }

  getTianmuScencePoint(tianmuControlInfo) {
    const modeData = this.getModeData(tianmuControlInfo);
    const isCirclePhysical = this.usePhysicalPixelMap && this.plan?.radius > 0;

    // pixel_map_physical：圆心为原点、y 向上；场景画布需左上角为原点、y 向下
    if (isCirclePhysical) {
      const radius = this.plan.radius;
      const diameter = radius * 2;

      Object.values(modeData).forEach((md) => {
        (md?.p?.dl || []).forEach((item) => {
          const x = Number(item.r.x) || 0;
          const y = Number(item.r.y) || 0;
          item.r.x = x + radius;
          item.r.y = radius - y;
        });
      });

      return {
        initData: {
          modeData,
          bg: {
            height: 900,
            url: "https://appresource.opple.com/api/icon/img/matrix_scene_recommend_image/matrixscene_",
            width: 900
          },
        },
        directionType: 3,
        shapType: 2,
        version: 1,
        planSize: { width: diameter, height: diameter },
      };
    }

    return {
      initData: {
        // appSize: { width: 1146, height: 1171 },
        modeData,
        bg: {
          height: 900,
          url: "https://appresource.opple.com/api/icon/img/matrix_scene_recommend_image/matrixscene_",
          width: 900
        },
        // bgSize: { width: 1146, height: 1146 },
        // stageSize: { height: 1171, width: 1146 }
      },
      directionType: 3,
      shapType: 2,
      version: 1,
      planSize: { width: this.blocks[0].length, height: this.blocks.length }
    }
  }

  getModeData(tianmuControlInfo) {
    const modeData = {}

    tianmuControlInfo.forEach((controller , index) => {
      if (!controller.device) return;
    
      const device = controller.device[0];
      let channelIndex = 0;

      if (device) {
        const VDeviceId = device.VDeviceId;
        
        this.controllers[index].channels.forEach(channel => {
          const key = controller.isDanlu ? VDeviceId : `${VDeviceId}-${channelIndex}`;
          modeData[key] = this.getMainPoint(channel)
          channelIndex += 1;
        });

        (this.controllers[index].slaves || []).forEach(slaveChannel => {
          slaveChannel.channels.forEach(channel => {
            const key = controller.isDanlu ? VDeviceId : `${VDeviceId}-${channelIndex}`;
            modeData[key] = this.getMainPoint(channel)
            
            channelIndex += 1;
          })
        });
      }
    })

    return modeData;
  }

  getMainPoint(planDevice) {
    const data = planDevice.data || [];
    const mainPoint = [];
    const pointCounts = [];
    const usePhysical = this.usePhysicalPixelMap;

    let currtDir = '';
    let count = 1;
    let dirCount = -1;

    data.forEach((item, index) => {
      const nextItem = data[index + 1];
      const coords = nextItem
        ? [item[0], item[1], nextItem[0], nextItem[1]]
        : null;
        
      const dir = !coords
        ? 'last'
        : (usePhysical
          ? this.getPhysicalDirType(coords, index)
          : this.getDirType(coords));

      if (dir !== currtDir) {
        mainPoint.push({
          f: "P",
          r: {x: item[0], y: item[1]},
          s: "C"
        });

        dirCount += 1;
        count += dirCount % 2 === 0 && dirCount !== 0 ? 0 : 1;
        pointCounts.push(count);
        count = dirCount % 2 === 0 ? 1 : 0;
        
      } else {
        count += 1;
      }

      currtDir = dir;
    })

    return {
      l: {
        c: planDevice.pointCount,
        l: pointCounts.slice(1),
        m: "SA"
      },
      p: {
        m: "L",
        dl: mainPoint
      }
    }
  }

  /** 物理坐标方向：45° 扇区合并同向；大跨度视为跨灯条新段 */
  getPhysicalDirType([stX, stY, endX, endY], index = 0) {
    const dx = endX - stX;
    const dy = endY - stY;
    const dist = Math.hypot(dx, dy);
    if (dist < 1e-6) return 'stay';

    const radius = this.plan?.radius || 2000;
    const jumpMm = Math.max(120, radius * 0.08);
    if (dist > jumpMm) {
      return `jump-${index}`;
    }

    const angle = Math.atan2(dy, dx);
    const sector = Math.round(angle / (Math.PI / 4));
    return `a${sector}`;
  }

  getDirType([
    stX, 
    stY, 
    endX, 
    endY
  ]) {
    let dir = '';

    if (stX === endX || stY === endY) {
      // 纵向
      if (stX === endX) {
        if (endY > stY) {
          dir = 'down';
        } else {
          dir = 'up';
        }
      } else {
        // 横向
        if (endX > stX) {
          dir = 'right';
        } else {
          dir = 'left';
        }
      }
    } else {
      dir = 'cross';
    }
    

    return dir;
  }
}

export default TianmuProxy;